/**
 * Waste Management Module
 *
 * @version 2.0
 * @updated 2026-02-23
 * @module core/modules/waste
 *
 * @description
 * Módulo de gestión de residuos municipales.
 * Incluye tracking GPS en tiempo real, gestión de vehículos,
 * contenedores, rutas y zonas de recolección.
 *
 * @remarks
 * **Características**:
 * - Gestión de flota de vehículos recolectores
 * - Tracking GPS en tiempo real
 * - Contenedores, rutas y zonas de cobertura
 * - Integración con sistema de modos operacionales
 *
 * **Auto-registro**:
 * Este módulo se auto-registra en el moduleRegistry al importarse.
 */

import { moduleRegistry } from '../../module-registry';
import { WasteTabbedPanel, WasteToolbarContent } from './components';
import { WasteProvider } from './WasteProvider';

// Types
export * from './types';

// Constants
export * from './constants';

// Stores
export {
  useWasteUIStore,
  useVehiclesStore,
  useContainersStore,
  useRoutesStore,
  type WasteUIStore,
  type VehiclesStore,
  type ContainersStore,
  type RoutesStore,
} from './store';

// Hooks
export {
  useWasteUI,
  useVehicles,
  useContainers,
  useRoutes,
  useRouteDrawing,
  useRoutesMapIntegration,
  useTrips,
  useGpsTracking,
} from './hooks';

// Services
export { wasteApi, getWasteApiErrorMessage, type WasteFilters } from './services';

// Config
export { WASTE_DRAWING_CONFIG, WASTE_DRAWING_CONFIG_MINIMAL } from './config/waste-drawing-config';

// Utilities - Geometry
export {
  calculateLineStringDistance,
  formatDistance,
  formatDuration,
  generateGeometryFromContainers,
  getBoundingBox,
  estimateRouteDuration,
  isValidLineString,
  optimizeContainerOrder,
  calculateOptimizationImprovement,
} from './utils/route-geometry-utils';

// Utilities - Validation
export {
  validateRouteCode,
  validateRouteName,
  validateRouteGeometry,
  validateContainerIds,
  validateEstimatedDistance,
  validateEstimatedDuration,
  validateStartTime,
  validateOperatingDays,
  validateCreateRouteDto,
  validateUpdateRouteDto,
  formatValidationErrors,
  formatValidationWarnings,
  extractErrorMessage,
  isNetworkError,
  isValidationError,
  getFriendlyErrorMessage,
  type ValidationResult,
} from './utils/route-validation';

// Map Layers
export {
  initializeRoutesLayer,
  updateRoutesData,
  highlightRoute,
  clearSelection,
  fitBoundsToRoute,
  setLayerVisibility,
  cleanupRoutesLayer,
  getCurrentProjectId,
  isInitialized,
} from './map-layers';

// Components
export { WasteToolbarContent, WasteTabbedPanel } from './components';

// Provider
export { WasteProvider } from './WasteProvider';

/* ===== AUTO-REGISTRO DEL MÓDULO ===== */

/**
 * Registra el módulo Waste en el registry
 *
 * @remarks
 * Este código se ejecuta cuando el módulo es importado.
 * El leftPanel es WasteTabbedPanel (panel con pestañas de residuos).
 * Incluye provider para WebSocket/realtime.
 */
moduleRegistry.register({
  id: 'waste',
  leftPanel: WasteTabbedPanel,
  toolbar: {
    content: WasteToolbarContent,
  },
  provider: WasteProvider,
  requiresRealtime: true,
  enabledByDefault: false,
});
